import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ToilCard from './toil/ToilCard';
import YourSetupCard from './YourSetupCard';
import { CalendarDays, Landmark, Users, CalendarCheck, Plane, TrendingUp, Lightbulb, ChevronRight } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import {
  format, parseISO, startOfWeek, endOfWeek, addWeeks, isWithinInterval, startOfDay,
} from 'date-fns';
import { leaveApi, holidaysApi, settingsApi, dashboardApi } from '../lib/api';
import { formatLeave, resolveUnit } from '../lib/leaveUnits';
import AllowanceExplainer from './AllowanceExplainer';
import NextLeaveYear from './NextLeaveYear';
import CarryOverLine from './CarryOverLine';
// ⚠️ Pure, and CHECKED — `node src/lib/bookingTime.checks.mjs` (back at work, times, lengths).
import { bookingLength, timeWindow, withTimes, backAtWork } from '../lib/bookingTime';

const initials = (name) =>
  (name || '?').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

// Collect distinct people with an approved leave block whose date falls in [from, to]
const peopleOffInRange = (wallchart, from, to) => {
  const map = new Map();
  (wallchart || []).forEach((entry) => {
    (entry.leave_blocks || []).forEach((b) => {
      if (b.status !== 'approved') return;
      const d = startOfDay(parseISO(b.date));
      if (isWithinInterval(d, { start: from, end: to })) {
        if (!map.has(entry.user_name)) {
          map.set(entry.user_name, {
            name: entry.user_name,
            profile_picture_url: entry.profile_picture_url,
            color: b.color || '#64748b',
            leave_type: b.leave_type || 'Leave',
            department: entry.department_name || '',
            start_date: b.start_date || b.date,
            end_date: b.end_date || b.date,
            // C16: a timed booking's window, so the line and "Back" are honest.
            start_minute: b.start_minute,
            end_minute: b.end_minute,
            // Carried through so "back at work" can use THEIR week — see returnToWork.
            working_days: entry.working_days || null,
          });
        }
      }
    });
  });
  return Array.from(map.values());
};

// When this person is back — THEIR next working day, not the next weekday (an employee in
// Algiers whose leave ends on a Thursday is back on Sunday), and the SAME day at its end
// time for a timed booking (C16). One rule, in lib/bookingTime.
const returnToWork = (p) => backAtWork(p, p.working_days);

// Colour-coded breakdown tooltip for the Team time off chart
const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const items = payload.filter((p) => (p.value || 0) > 0);
  const total = items.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-sm">
      {items.length === 0 ? (
        <div><span className="font-medium">{label}</span> <span className="text-muted-foreground">&mdash; no time off</span></div>
      ) : (
        <>
          <div className="font-medium mb-1">{label} &mdash; {total} {total === 1 ? 'day' : 'days'}</div>
          {items.map((p) => (
            <div key={p.name} className="flex items-center gap-2 leading-5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color || p.fill }} />
              <span>{p.value} {p.value === 1 ? 'Day' : 'Days'} {p.name}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const DashboardOverview = ({ user, refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [annual, setAnnual] = useState(null);
  const [sickUsed, setSickUsed] = useState({ days: 0, minutes: null });
  const [bhIncluded, setBhIncluded] = useState(false);
  const [bh, setBh] = useState({ remaining: 0, passed: 0, total: 0, next: null });
  const [wallchart, setWallchart] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [trends, setTrends] = useState([]);
  const [trendTypes, setTrendTypes] = useState([]);
  const [trendLabel, setTrendLabel] = useState('');
  const [whoTab, setWhoTab] = useState('today');
  // For the "use it or lose it" tip only: the leave year's start and the carry-over cap.
  const [mySetup, setMySetup] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const year = new Date().getFullYear();
      const today = startOfDay(new Date());
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const nextWeekEnd = endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
      try {
        const [allowRes, holRes, setRes, trendRes, upRes, wcRes, mySetupRes] = await Promise.all([
          // ⚠️ No year: the server's default is the company's current LEAVE year. Sending
          // the calendar year here showed NEXT year's balance at an April company every
          // January (DESIGN-leave-years.md). `year` below is still right for the holidays.
          leaveApi.getMyAllowances().catch(() => ({ data: [] })),
          holidaysApi.list(year, user?.id).catch(() => ({ data: [] })),
          settingsApi.get().catch(() => ({ data: {} })),
          dashboardApi.getTeamTimeOff().catch(() => ({ data: { monthly_totals: [] } })),
          dashboardApi.getMyUpcomingLeave(30).catch(() => ({ data: { upcoming: [] } })),
          dashboardApi.getWallchart(format(weekStart, 'yyyy-MM-dd'), format(nextWeekEnd, 'yyyy-MM-dd')).catch(() => ({ data: [] })),
          dashboardApi.getMySetup().catch(() => ({ data: null })),
        ]);

        const allowances = allowRes.data || [];
        setAnnual(allowances.find((a) => a.leave_type_name === 'Annual Leave') || null);
        const _sick = allowances.find((a) => a.leave_type_name === 'Sick Leave') || {};
        setSickUsed({ days: _sick.used || 0, minutes: _sick.used_minutes });
        setBhIncluded(!!(setRes.data && setRes.data.bank_holidays_included));

        const hols = (holRes.data || []).slice().sort((a, b) => a.date.localeCompare(b.date));
        const todayStr = format(today, 'yyyy-MM-dd');
        const passed = hols.filter((h) => h.date < todayStr);
        const remaining = hols.filter((h) => h.date >= todayStr);
        setBh({ total: hols.length, passed: passed.length, remaining: remaining.length, next: remaining[0] || null });

        setTrends((trendRes.data && trendRes.data.monthly_totals) || []);
        setTrendTypes((trendRes.data && trendRes.data.types) || []);
        setTrendLabel((trendRes.data && trendRes.data.label) || '');
        // ⚠️ Already only this person's own leave — the SERVER filters it now. Never go back
        // to fetching everyone's and filtering here (DESIGN-reports.md finding 9).
        setUpcoming((upRes.data && upRes.data.upcoming) || []);
        setWallchart(wcRes.data || []);
        setMySetup(mySetupRes.data || null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id, refreshKey]);

  // Keyed on the DATE STRING, not the Date object.
  //
  // `new Date()` returns a fresh object every render, so a Date in a dependency array
  // never compares equal and the memo below would recompute constantly — which is why
  // these dates were previously left out of the deps entirely. That fixed the churn but
  // introduced a real bug: leave a dashboard open across midnight and "Who's off today"
  // kept showing yesterday, because nothing told the memo the day had changed.
  //
  // Keying on 'yyyy-MM-dd' gives a value that's stable within a day and changes exactly
  // once when it rolls over. Now the dates can go in the deps honestly.
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  // todayKey isn't used inside the callback, so the rule calls it unnecessary — but it
  // IS the point: it's the cache key that makes this recompute exactly once a day.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => startOfDay(new Date()), [todayKey]);
  const weekStart = useMemo(() => startOfWeek(today, { weekStartsOn: 1 }), [today]);
  const weekEnd = useMemo(() => endOfWeek(today, { weekStartsOn: 1 }), [today]);
  const nextWeekStart = useMemo(() => startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 }), [today]);
  const nextWeekEnd = useMemo(() => endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 }), [today]);

  const offToday = useMemo(() => peopleOffInRange(wallchart, today, today), [wallchart, today]);
  const offThisWeek = useMemo(() => peopleOffInRange(wallchart, weekStart, weekEnd), [wallchart, weekStart, weekEnd]);
  const offNextWeek = useMemo(() => peopleOffInRange(wallchart, nextWeekStart, nextWeekEnd), [wallchart, nextWeekStart, nextWeekEnd]);

  const allowanceTotal = annual ? (annual.total_allowance || 0) + (annual.carried_over || 0) : 0;
  const used = annual ? (annual.used || 0) : 0;
  // `remaining` from the API is ALREADY net of bank holidays (D8 — they are deducted
  // from the pot server-side, once). This used to subtract them again on the client
  // when "included" was set:
  //     daysRemaining = bhIncluded ? Math.max(0, rawRemaining - bh.remaining) : rawRemaining
  // That fudge existed in FIVE places, each with subtly different inputs. Doing it now
  // would DOUBLE-COUNT. The server is the single source of truth for this number.
  const daysRemaining = annual ? (annual.remaining || 0) : 0;

  // Display unit + canonical minutes for the same three figures. The bar above stays a
  // RATIO of days — used/total is the same number in either unit, so there is nothing
  // to convert and nothing to get wrong.
  //
  // `totalMinutes` mirrors `allowanceTotal` (grant + carry-over), and needs BOTH parts:
  // if carried_over_minutes were missing while carried_over days weren't, the hours
  // total would quietly understate the days total sitting right next to it.
  const _tm = annual ? annual.total_allowance_minutes : null;
  const _cm = annual ? annual.carried_over_minutes : null;
  const totalMinutes = (_tm !== null && _tm !== undefined && _cm !== null && _cm !== undefined)
    ? _tm + _cm
    : null;
  const remainingMinutes = annual ? annual.remaining_minutes : null;
  const usedMinutes = annual ? annual.used_minutes : null;
  // One unit for the whole card: if ANY figure lacks minutes, the card shows days
  // throughout rather than "16.5 of 255h".
  const unit = resolveUnit(annual && annual.display_unit, totalMinutes, remainingMinutes, usedMinutes);
  // The "This year so far" card is its own unit scope: it pairs holiday-used with
  // sick-used, and Sick Leave is a separate allowance row that may not be backfilled
  // even when Annual Leave is.
  const yearCardUnit = resolveUnit(annual && annual.display_unit, usedMinutes, sickUsed.minutes);

  const whoMap = { today: offToday, this: offThisWeek, next: offNextWeek };
  const whoList = whoMap[whoTab] || [];

  if (loading) {
    return (
      <div className="card-hive p-8 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ---- The holiday ring (Warm Hive, 28/10/2026) ----
  // The orange arc is what's LEFT of the allowance — the same `remaining` the number in
  // the middle shows, never recomputed here (see the note on daysRemaining above).
  const ringShare = allowanceTotal > 0 ? Math.max(0, Math.min(1, daysRemaining / allowanceTotal)) : 0;
  const RING_R = 52;
  // What's left, as the ring shows it — and whether it is hours AND minutes, which stack (Rob B, 02/11/2026).
  const ringLeft = formatLeave(daysRemaining, { unit, minutes: remainingMinutes, withUnit: false });
  const ringHM = /^(\S+)\s+(\S+)$/.exec(String(ringLeft));
  const ringLength = 2 * Math.PI * RING_R;
  const grantDays = annual ? (annual.total_allowance || 0) : 0;
  const carriedDays = annual ? (annual.carried_over || 0) : 0;

  // ---- "Use it or lose it" (Rob, 28/10/2026, 2A) ----
  // Only in the last three months of the leave year, and only when more is left than can
  // be carried over. All year round it would nag people in January. Words only.
  const useItTip = (() => {
    if (!mySetup || !(daysRemaining > 0)) return null;
    const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
      'August', 'September', 'October', 'November', 'December'];
    const startIdx = Math.max(0, MONTHS.indexOf(mySetup.leave_year?.start_month || 'January'));
    let nextStart = new Date(today.getFullYear(), startIdx, 1);
    if (nextStart <= today) nextStart = new Date(today.getFullYear() + 1, startIdx, 1);
    const daysToGo = Math.round((nextStart - today) / 86400000);
    if (daysToGo > 92) return null;
    const carry = mySetup.carry_over || {};
    if (carry.enabled && !(carry.max_days > 0)) return null;           // no limit — nothing to lose
    if (carry.enabled && daysRemaining <= carry.max_days) return null;  // all of it carries over
    const lastDay = new Date(nextStart.getFullYear(), nextStart.getMonth(), 0);
    return {
      left: formatLeave(daysRemaining, { unit, minutes: remainingMinutes }),
      by: format(lastDay, 'd MMMM'),
      after: carry.enabled
        ? `Up to ${carry.max_days} ${carry.max_days === 1 ? 'day' : 'days'} can carry over.`
        : "Unused days don't carry over.",
    };
  })();

  // ---- Team time off: a total above each bar ----
  // The month's total sits on whichever segment is on top THAT month: `_top0`, `_top1`…
  // carry the total only for the series that is highest that month. (A label tied to the
  // last series vanished whenever that series was empty — checked in the browser,
  // 28/10/2026: only the two months with sick leave showed a total.)
  const trendsWithTotals = trends.map((m) => {
    const total = trendTypes.length === 0
      ? (m.days || 0)
      : trendTypes.reduce((sum, t) => sum + (m[t.name] || 0), 0);
    const row = { ...m, _total: total };
    let top = -1;
    trendTypes.forEach((t, i) => { if ((m[t.name] || 0) > 0) top = i; });
    trendTypes.forEach((t, i) => { row[`_top${i}`] = i === top && total > 0 ? total : null; });
    return row;
  });
  // Rob, 28/10/2026: a company-wide chart is for the people who run the team, not for
  // every employee. Presentation only — the data behind it is unchanged.
  const showTeamChart = user?.role !== 'employee';


  return (
    <div className="space-y-4">
      {/* Personal summary. A flex row on wide screens so it holds three cards or four
          (time in lieu only appears when the company uses it). */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:flex gap-4">
        {/* Days remaining — the navy card. `dark` makes everything inside it take the
            dark palette, so the explainer, carry-over line and next-year block stay
            readable on navy without touching those components. */}
        <div className="dark relative overflow-hidden rounded-xl bg-hive-navy dark:bg-[#1C2B52] text-foreground p-5 sm:p-6 md:col-span-2 lg:flex-[1.55] min-w-0">
          <div aria-hidden="true" className="pointer-events-none absolute -right-8 -top-10 w-36 h-36 bg-hive-orange/10 [clip-path:polygon(25%_5%,75%_5%,100%_50%,75%_95%,25%_95%,0_50%)]" />
          <div aria-hidden="true" className="pointer-events-none absolute right-10 -bottom-16 w-28 h-28 bg-white/5 [clip-path:polygon(25%_5%,75%_5%,100%_50%,75%_95%,25%_95%,0_50%)]" />
          <div className="relative flex items-center gap-5">
            <div className="relative w-28 h-28 flex-shrink-0">
              <svg viewBox="0 0 128 128" className="w-28 h-28 -rotate-90" aria-hidden="true">
                <circle cx="64" cy="64" r={RING_R} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="13" />
                {ringShare > 0 && (
                  <circle cx="64" cy="64" r={RING_R} fill="none" stroke="#F7871F" strokeWidth="13"
                    strokeLinecap="round" strokeDasharray={`${ringLength * ringShare} ${ringLength}`} />
                )}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
                {/* Hours AND minutes ("129h 30m") don't fit the ring on one line at the size made for
                    days, so they stack: hours, then minutes smaller (Rob B, 02/11/2026). Sizes MEASURED to
                    sit inside the ring, "300h 45m of 337h 30m" included. Days, and whole hours ("180h"),
                    keep the big single figure. */}
                {ringHM ? (
                  <>
                    <span className="text-[19px] leading-none font-heading font-bold text-white" data-testid="ring-hours">{ringHM[1]}</span>
                    <span className="text-[12px] leading-none font-heading font-semibold text-white mt-0.5" data-testid="ring-minutes">{ringHM[2]}</span>
                  </>
                ) : (
                  <span className="text-[28px] leading-none font-heading font-bold text-white">{ringLeft}</span>
                )}
                <span className={`${ringHM ? 'text-[10px]' : 'text-[11px]'} text-muted-foreground mt-1`}>
                  of {formatLeave(allowanceTotal, { unit, minutes: totalMinutes })}
                </span>
              </div>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-heading text-[17px] font-semibold text-white">Your holiday remaining</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                <span className="text-foreground font-bold">{formatLeave(grantDays, { unit, minutes: _tm })}</span> allowance
                {carriedDays > 0 && (
                  <> <span className="text-foreground font-bold">+ {formatLeave(carriedDays, { unit, minutes: _cm })}</span> carried over</>
                )}
              </p>
              <p className="text-sm text-muted-foreground">{formatLeave(used, { unit, minutes: usedMinutes })} booked so far</p>
              {bh.remaining > 0 && (
                <p className="text-sm text-muted-foreground">
                  Plus <span className="text-hive-orange font-bold">{bh.remaining} bank holiday{bh.remaining === 1 ? '' : 's'}</span> to come
                </p>
              )}
              {/* How the number was worked out — words, not a grey "?" (Rob D, 01/11/2026). */}
              <div className="mt-2">
                <AllowanceExplainer withWords />
              </div>
            </div>
          </div>
          {/* Carry-over use-by (DESIGN-rollover.md, 09/10/2026) — only before the date. */}
          <CarryOverLine allowance={annual} />
          {/* DESIGN-leave-years.md decision H (option B): only when next year has leave in it. */}
          <NextLeaveYear reloadKey={`${user?.id}-${refreshKey}`} />
        </div>

        {/* Booked / sick */}
        <div className="card-hive p-5 lg:flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 text-muted-foreground font-semibold">
            <CalendarCheck className="w-4 h-4 text-hive-orange" />
            <span className="text-sm">This year so far</span>
          </div>
          <div className="flex items-end justify-between mt-auto pt-6">
            <div>
              <p className="text-[32px] leading-tight font-heading font-bold">{formatLeave(used, { unit: yearCardUnit, minutes: usedMinutes, withUnit: false })}</p>
              <p className="text-sm text-muted-foreground">Holiday booked</p>
            </div>
            <div className="text-right border-l border-border pl-5">
              <p className="text-[32px] leading-tight font-heading font-bold">{formatLeave(sickUsed.days, { unit: yearCardUnit, minutes: sickUsed.minutes, withUnit: false })}</p>
              {/* "Sick leave", not "Sick days" — the figure above it is hours for anyone
                  on the hours unit, and a "days" label over "12h" is just wrong. */}
              <p className="text-sm text-muted-foreground">Sick leave</p>
            </div>
          </div>
        </div>

        {/* Bank holidays */}
        <div className="card-hive p-5 lg:flex-1 min-w-0">
          <div className="flex items-center gap-2 text-muted-foreground font-semibold">
            <Landmark className="w-4 h-4 text-hive-orange" />
            <span className="text-sm">Bank holidays ({new Date().getFullYear()})</span>
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-[32px] leading-tight font-heading font-bold">{bh.remaining}</span>
            <span className="text-sm text-muted-foreground">of {bh.total} to come</span>
          </div>
          {bh.next ? (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">Next up</p>
              <p className="text-sm font-bold truncate" title={bh.next.name}>{bh.next.name}</p>
              <p className="text-xs font-bold text-hive-burnt dark:text-hive-orange">{format(parseISO(bh.next.date), 'EEE d MMM')}</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-2">No more bank holidays this year</p>
          )}
        </div>

        {/* Time in lieu — its own balance beside holiday (DESIGN-toil.md J). Nothing when off.
            The fourth card in the row (Rob, 28/10/2026, 1A), not a full-width box. */}
        <ToilCard refreshKey={refreshKey} className="lg:flex-1 min-w-0" />
      </div>

      {/* Who's off + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Who's off */}
        <div className="card-hive p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-hive-orange" />
            <h3 className="font-heading text-[17px] font-semibold">Who's off</h3>
          </div>
          <div className="flex gap-1 mb-3 bg-background dark:bg-secondary rounded-xl p-1 w-fit">
            {[
              { k: 'today', label: `Today (${offToday.length})` },
              { k: 'this', label: `This week (${offThisWeek.length})` },
              { k: 'next', label: `Next week (${offNextWeek.length})` },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setWhoTab(t.k)}
                className={`text-[13px] px-3 py-1.5 rounded-lg transition-colors ${whoTab === t.k ? 'bg-card shadow-sm font-bold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {whoList.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No one is off {whoTab === 'today' ? 'today' : whoTab === 'this' ? 'this week' : 'next week'} 🎉</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {whoList.map((p) => {
                const ret = returnToWork(p);
                return (
                <div key={p.name} className="flex items-center gap-3 rounded-[14px] border border-border bg-card px-3.5 py-2.5">
                  <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#64748b' }} />
                  {p.profile_picture_url ? (
                    <img src={p.profile_picture_url} alt={p.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {initials(p.name)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold truncate">{p.name}</p>
                      <span className="text-[10.5px] px-2 py-0.5 rounded-full font-bold flex-shrink-0" style={{ backgroundColor: (p.color || '#64748b') + '22', color: p.color || '#64748b' }}>
                        {p.leave_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5 min-w-0">
                      {p.department && <span className="truncate">{p.department}</span>}
                      {p.department && <span className="text-border flex-shrink-0">•</span>}
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <CalendarDays className="w-3 h-3" />
                        {p.start_date && p.end_date && p.start_date !== p.end_date
                          ? `${format(parseISO(p.start_date), 'd MMM')} – ${format(parseISO(p.end_date), 'd MMM')}`
                          : withTimes(format(parseISO(p.start_date), 'EEE d MMM'), p)}
                      </span>
                    </div>
                  </div>
                  {ret && (
                    <div className="text-right flex-shrink-0 pl-3 ml-1 border-l border-border">
                      <p className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">Back</p>
                      <p className="text-xs font-bold">{ret.time || format(ret.date, 'EEE d MMM')}</p>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
          {/* The whole month for everyone (Warm Hive decision 9; Rob's 6A, 29/10/2026: it
              opens on this month with today marked — no new filter). */}
          <Link
            to="/team-calendar"
            className="inline-flex items-center gap-1 mt-3 text-[13.5px] font-bold text-hive-burnt dark:text-hive-orange hover:underline"
            data-testid="whos-off-team-calendar-link"
          >
            See the team calendar <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Your upcoming leave */}
        <div className="card-hive p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Plane className="w-5 h-5 text-hive-orange" />
            <h3 className="font-heading text-[17px] font-semibold">Your upcoming leave</h3>
            <span className="text-xs text-muted-foreground ml-auto">next 30 days</span>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">You have no leave booked in the next 30 days</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {upcoming.slice(0, 12).map((u) => (
                <div key={u.id} className="flex items-center gap-3 rounded-[14px] bg-background dark:bg-secondary px-4 py-3">
                  <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: u.leave_type_color || '#64748b' }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate">{u.leave_type}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {timeWindow(u) ? `${timeWindow(u)} · ${bookingLength(u)}` : bookingLength(u)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold">{format(parseISO(u.start_date), 'EEE d MMM')}{u.start_date !== u.end_date ? ` – ${format(parseISO(u.end_date), 'EEE d MMM')}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {useItTip && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-hive-pending-bg dark:bg-secondary px-3.5 py-2.5 text-[13px]" data-testid="use-it-tip">
              <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0 text-hive-burnt dark:text-hive-orange" />
              <span>
                You still have <span className="font-bold text-hive-pending-text dark:text-hive-orange">{useItTip.left}</span> to book by {useItTip.by}. {useItTip.after}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Your setup — always open, under Who's off (Rob, 28/10/2026). */}
      <YourSetupCard />

      {/* Team time off chart — admins and managers only (Rob, 28/10/2026). */}
      {showTeamChart && (
      <div className="card-hive p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-hive-orange" />
            <h3 className="font-heading text-[17px] font-semibold">Team time off{trendLabel ? ` — ${trendLabel}` : ''}</h3>
          </div>
          {trendTypes.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground sm:ml-auto">
              {trendTypes.map((t) => (
                <span key={t.name} className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: t.color }} />
                  {t.name}
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={trendsWithTotals} margin={{ top: 18, right: 8, left: -16, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: 'hsl(var(--secondary))' }} content={<TrendTooltip />} />
              {trendTypes.length === 0 ? (
                <Bar dataKey="days" radius={[6, 6, 0, 0]} fill="hsl(var(--primary))">
                  <LabelList dataKey="_total" position="top" fontSize={11} fontWeight={700} fill="hsl(var(--muted-foreground))" formatter={(v) => (v > 0 ? v : '')} />
                </Bar>
              ) : (
                trendTypes.map((t, i) => (
                  <Bar
                    key={t.name}
                    dataKey={t.name}
                    stackId="off"
                    fill={t.color}
                    radius={i === trendTypes.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                  >
                    <LabelList dataKey={`_top${i}`} position="top" fontSize={11} fontWeight={700} fill="hsl(var(--muted-foreground))" formatter={(v) => (v ? v : '')} />
                  </Bar>
                ))
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}
    </div>
  );
};

export default DashboardOverview;
